// src/components/ui/PhotoUpload.tsx
// ✅ LOGGING: Complete migration to centralized logging system

import React, { useState, useRef, useCallback } from 'react';
import { Camera, Upload, X, Trash2, Eye, AlertCircle, CheckCircle } from 'lucide-react';
import { photoService, PhotoUploadProgress } from '../../services/photoService';
import { logger } from '../../utils/logger';

// ✅ LOGGING: Create dedicated logger for Photo Upload component
const photoUploadLogger = logger.createServiceLogger('PhotoUpload');

interface PhotoUploadProps {
  itemId?: string;
  photos?: string[];
  existingPhotos?: string[];
  onPhotosUpdate?: (photos: string[]) => void;
  onPhotosChange?: (photos: string[]) => void;
  disabled?: boolean;
  maxPhotos?: number;
  className?: string;
}

interface UploadStatus {
  uploading: boolean;
  progress: number;
  error?: string;
  success?: boolean;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  itemId = 'temp-item',
  photos = [],
  existingPhotos = [],
  onPhotosUpdate,
  onPhotosChange,
  disabled = false,
  maxPhotos = 5,
  className = '',
}) => {
  // Use either photos or existingPhotos, with photos taking priority
  const currentPhotos = photos.length > 0 ? photos : existingPhotos;
  
  // Use the appropriate callback function
  const updatePhotos = onPhotosUpdate || onPhotosChange || (() => {});

  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    uploading: false,
    progress: 0,
  });
  const [dragOver, setDragOver] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(async (files: FileList) => {
    const timer = logger.startTimer('PhotoUpload', 'handleFileSelect');
    
    photoUploadLogger.info('File selection initiated', {
      fileCount: files.length,
      firstFileName: files[0]?.name,
      firstFileSize: files[0]?.size,
      firstFileType: files[0]?.type,
      disabled,
      currentPhotoCount: currentPhotos.length,
      maxPhotos,
      itemId
    });

    if (disabled || files.length === 0) {
      photoUploadLogger.warn('Upload process terminated early', {
        reason: disabled ? 'component disabled' : 'no files selected',
        disabled,
        fileCount: files.length
      });
      timer();
      return;
    }

    const file = files[0];
    photoUploadLogger.debug('Starting file validation', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      lastModified: file.lastModified
    });
    
    const validation = photoService.validateFile(file);
    photoUploadLogger.debug('File validation completed', {
      fileName: file.name,
      isValid: validation.valid,
      validationError: validation.error || null
    });

   if (!validation.valid) {
      photoUploadLogger.warn('File validation failed', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        validationError: validation.error
      });
      
      setUploadStatus({
        uploading: false,
        progress: 0,
        error: validation.error,
      });
      timer();
      return;
    }

    if (currentPhotos.length >= maxPhotos) {
      const errorMessage = `Maximum ${maxPhotos} photos allowed`;
      photoUploadLogger.warn('Upload blocked - photo limit reached', {
        currentPhotoCount: currentPhotos.length,
        maxPhotos,
        fileName: file.name
      });
      
      setUploadStatus({
        uploading: false,
        progress: 0,
        error: errorMessage,
      });
      timer();
      return;
    }

    photoUploadLogger.info('Starting photo upload process', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      itemId,
      currentPhotoCount: currentPhotos.length,
      targetPhotoCount: currentPhotos.length + 1
    });
    
    setUploadStatus({ uploading: true, progress: 0 });

    try {
      const photoUrl = await photoService.uploadPhoto(
        file,
        itemId,
        (progress: PhotoUploadProgress) => {
          photoUploadLogger.debug('Upload progress update', {
            fileName: file.name,
            percentage: progress.percentage,
            loaded: progress.loaded,
            total: progress.total,
            bytesRemaining: progress.total - progress.loaded
          });
          
          setUploadStatus(prev => ({
            ...prev,
            progress: progress.percentage,
          }));
        }
      );

      photoUploadLogger.info('Photo upload completed successfully', {
        fileName: file.name,
        fileSize: file.size,
        photoUrl,
        uploadDuration: `${Date.now() - (timer as any).startTime}ms`,
        finalPhotoCount: currentPhotos.length + 1
      });
      
      const updatedPhotos = [...currentPhotos, photoUrl];
      updatePhotos(updatedPhotos as string[]);

      setUploadStatus({
        uploading: false,
        progress: 100,
        success: true,
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setUploadStatus({ uploading: false, progress: 0 });
      }, 3000);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      photoUploadLogger.error('Photo upload failed', {
        fileName: file.name,
        fileSize: file.size,
        itemId,
        error: errorMessage,
        currentPhotoCount: currentPhotos.length
      }, error instanceof Error ? error : undefined);
      
      setUploadStatus({
        uploading: false,
        progress: 0,
        error: errorMessage,
      });
    } finally {
      timer();
    }
  }, [disabled, currentPhotos, maxPhotos, itemId, updatePhotos]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    photoUploadLogger.debug('File input change detected', {
      fileCount: e.target.files?.length || 0,
      hasFiles: !!e.target.files && e.target.files.length > 0
    });
    
    if (e.target.files) {
      handleFileSelect(e.target.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (disabled) return;

    const files = e.dataTransfer.files;
    photoUploadLogger.debug('Files dropped via drag and drop', {
      fileCount: files.length,
      disabled
    });
    
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleDeletePhoto = async (photoUrl: string) => {
    if (disabled) return;

    const timer = logger.startTimer('PhotoUpload', 'deletePhoto');
    
    photoUploadLogger.info('Initiating photo deletion', {
      photoUrl: photoUrl.substring(0, 50) + '...', // Truncate URL for logging
      currentPhotoCount: currentPhotos.length,
      itemId
    });

    try {
      await photoService.deletePhoto(photoUrl);
      const updatedPhotos = currentPhotos.filter(p => p !== photoUrl);
      
      photoUploadLogger.info('Photo deleted successfully', {
        photoUrl: photoUrl.substring(0, 50) + '...',
        previousPhotoCount: currentPhotos.length,
        newPhotoCount: updatedPhotos.length
      });
      
      updatePhotos(updatedPhotos);
    } catch (error) {
      const errorMessage = 'Failed to delete photo';
      photoUploadLogger.error('Photo deletion failed', {
        photoUrl: photoUrl.substring(0, 50) + '...',
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      
      setUploadStatus({
        uploading: false,
        progress: 0,
        error: errorMessage,
      });
    } finally {
      timer();
    }
  };

  const openFileDialog = () => {
    photoUploadLogger.debug('File dialog requested', {
      disabled,
      hasFileInputRef: !!fileInputRef.current
    });
    
    if (!disabled && fileInputRef.current) {
      photoUploadLogger.debug('Triggering file input dialog');
      fileInputRef.current.click();
    }
  };

  const clearError = () => {
    photoUploadLogger.debug('Error state cleared by user');
    setUploadStatus({ uploading: false, progress: 0 });
  };

  // Component render logging
  photoUploadLogger.debug('PhotoUpload component rendering', {
    itemId,
    currentPhotoCount: currentPhotos.length,
    maxPhotos,
    disabled,
    isUploading: uploadStatus.uploading,
    hasError: !!uploadStatus.error,
    uploadProgress: uploadStatus.progress
  });

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          transition-all duration-200 min-h-32
          ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${uploadStatus.uploading ? 'pointer-events-none' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileInputChange}
          disabled={disabled}
        />

        {uploadStatus.uploading ? (
          <div className="space-y-3">
            <div className="flex items-center justify-center">
              <Upload className="w-8 h-8 text-blue-500 animate-pulse" />
            </div>
            <div className="text-sm text-gray-600">
              Uploading photo... {uploadStatus.progress}%
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadStatus.progress}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-center">
              <Camera className="w-8 h-8 text-gray-400" />
            </div>
            <div className="text-sm text-gray-600">
              <div>Click to upload or drag and drop</div>
              <div className="text-xs text-gray-500 mt-1">
                JPEG, PNG, WebP up to 10MB
              </div>
            </div>
            {currentPhotos.length > 0 && (
              <div className="text-xs text-gray-500">
                {currentPhotos.length} of {maxPhotos} photos uploaded
              </div>
            )}
          </div>
        )}
      </div>

      {/* Status Messages */}
      {uploadStatus.error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm text-red-700">{uploadStatus.error}</span>
          </div>
          <button
            onClick={clearError}
            className="text-red-500 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {uploadStatus.success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3 flex items-center space-x-2">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-sm text-green-700">Photo uploaded successfully!</span>
        </div>
      )}

      {/* Photo Gallery */}
      {currentPhotos.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Uploaded Photos ({currentPhotos.length})
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {currentPhotos.map((photoUrl, index) => (
              <div
                key={index}
                className="relative group bg-gray-100 rounded-lg overflow-hidden aspect-square"
              >
                <img
                  src={photoUrl}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    photoUploadLogger.warn('Photo failed to load, using fallback', {
                      photoUrl: photoUrl.substring(0, 50) + '...',
                      photoIndex: index
                    });
                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTkgMTJMMTEgMTRMMTUgMTBNMjEgMTJDMjEgMTYuOTcwNiAxNi45NzA2IDIxIDEyIDIxQzcuMDI5NDQgMjEgMyAxNi45NzA2IDMgMTJDMyA3LjAyOTQ0IDcuMDI5NDQgMyAxMiAzQzE2Ljk3MDYgMyAyMSA3LjAyOTQ0IDIxIDEyWiIgc3Ryb2tlPSIjOTk5IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K';
                  }}
                />
                
                {/* Photo Actions */}
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      photoUploadLogger.debug('Photo preview requested', {
                        photoIndex: index,
                        photoUrl: photoUrl.substring(0, 50) + '...'
                      });
                      setSelectedPhoto(photoUrl);
                    }}
                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                    title="View photo"
                  >
                    <Eye className="w-4 h-4 text-gray-700" />
                  </button>
                  
                  {!disabled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePhoto(photoUrl);
                      }}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="Delete photo"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photo Modal */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => {
                photoUploadLogger.debug('Photo modal closed');
                setSelectedPhoto(null);
              }}
              className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors z-10"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={selectedPhoto}
              alt="Full size"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;